import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from '../design-system/atoms/Spinner';
import { fetchInvoice, fetchPublicCompanyInfo, fetchCompanyBranding, fetchInvoiceClientName } from '../domains/invoices/api';
import type { Invoice } from '../domains/invoices/types';
import type { PublicCompanyInfo, CompanyBranding } from '../domains/invoices/api';
import { numericOrderId } from '../shared/utils/ids';

function fmt(amount: number): string {
  return amount.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

const STATUS_LABELS: Record<string, string> = { draft: 'Черновик', sent: 'Выставлен', paid: 'Оплачен', cancelled: 'Отменён' };
const STATUS_COLORS: Record<string, string> = { draft: '#6B7280', sent: '#D97706', paid: '#16A34A', cancelled: '#DC2626' };
const PAYMENT_LABELS: Record<string, string> = { cash: 'Наличными', transfer: 'Банковским переводом', card: 'Картой', qr: 'По СБП' };

const BG = '#F7F8FA', CARD = '#FFFFFF', BORDER = '#E5E7EB', TEXT = '#111827', SUB = '#6B7280';

function ReqRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
      <span style={{ fontSize: 12, color: SUB, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: TEXT, fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

export function InvoicePage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [company, setCompany] = useState<PublicCompanyInfo | null>(null);
  const [branding, setBranding] = useState<CompanyBranding>({ stamp_url: null, signature_url: null });
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!invoiceId) return;
    fetchInvoice(invoiceId)
      .then(async (inv) => {
        if (!inv) { setNotFound(true); return; }
        setInvoice(inv);
        const [info, brand, client] = await Promise.all([
          fetchPublicCompanyInfo(inv.company_id).catch(() => null),
          fetchCompanyBranding(inv.company_id).catch(() => ({ stamp_url: null, signature_url: null })),
          fetchInvoiceClientName(inv.id).catch(() => ''),
        ]);
        setCompany(info);
        setBranding(brand);
        setClientName(client);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  if (loading) return <div style={{ minHeight: '100svh', background: BG, display: 'grid', placeItems: 'center' }}><Spinner label="Загружаем счёт..." /></div>;

  if (notFound || !invoice) return (
    <div style={{ minHeight: '100svh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui,sans-serif' }}>
      <div style={{ textAlign: 'center', color: SUB }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
        <div style={{ fontSize: 18, color: TEXT, fontWeight: 600, marginBottom: 8 }}>Счёт не найден</div>
        <div style={{ fontSize: 14 }}>Возможно, ссылка устарела или была изменена.</div>
      </div>
    </div>
  );

  const items = invoice.items ?? [];
  const total = invoice.total_amount;
  const isAkt = (searchParams.get('doc') ?? '').toLowerCase() === 'akt';
  const isPaid = invoice.status === 'paid';
  const statusColor = STATUS_COLORS[invoice.status] ?? SUB;
  const companyName = company?.name || 'Компания';
  const pd = company?.payment_details;
  const hasBankDetails = pd && (pd.account || pd.bank_name);
  const hasSbp = pd?.sbp_phone;

  return (
    <div style={{ minHeight: '100svh', background: BG, fontFamily: "'Inter', system-ui, sans-serif", color: TEXT }}>
      {/* Print stylesheet: скрываем кнопку, убираем фон, формат под A4. */}
      <style>{`
        @media print {
          .fs-no-print { display: none !important; }
          body { background: #fff !important; }
          @page { size: A4; margin: 14mm; }
        }
      `}</style>

      <div style={{ maxWidth: 520, margin: '0 auto', paddingBottom: 60 }}>

        {/* Кнопка скачивания/печати + переключатель Счёт/Акт — не в PDF */}
        <div className="fs-no-print" style={{ padding: '16px 16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <a
              href="?"
              onClick={(e) => { e.preventDefault(); setSearchParams({}); }}
              style={{
                padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                textDecoration: 'none', cursor: 'pointer',
                border: `1px solid ${isAkt ? BORDER : '#F2542D'}`,
                background: isAkt ? CARD : '#FEF1ED',
                color: isAkt ? SUB : '#F2542D',
              }}
            >Счёт</a>
            <a
              href="?doc=akt"
              onClick={(e) => { e.preventDefault(); setSearchParams({ doc: 'akt' }); }}
              style={{
                padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                textDecoration: 'none', cursor: 'pointer',
                border: `1px solid ${isAkt ? '#F2542D' : BORDER}`,
                background: isAkt ? '#FEF1ED' : CARD,
                color: isAkt ? '#F2542D' : SUB,
              }}
            >Акт</a>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              border: 'none',
              background: '#F2542D',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 8px rgba(242,84,45,0.3)',
            }}
          >
            <span style={{ fontSize: 15 }}>⬇</span>
            Скачать PDF / Печать
          </button>
        </div>

        {/* Header */}
        <div style={{ background: CARD, borderBottom: `1px solid ${BORDER}`, padding: '24px 24px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            {company?.logo_url ? (
              <img src={company.logo_url} alt={companyName} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', border: `1px solid ${BORDER}` }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#111827', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{companyName[0]?.toUpperCase()}</span>
              </div>
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: TEXT }}>{companyName}</div>
              {company?.phone && <div style={{ fontSize: 12, color: SUB, marginTop: 2 }}>{company.phone}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: SUB, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 4 }}>{isAkt ? 'АКТ ВЫПОЛНЕННЫХ РАБОТ' : 'СЧЁТ НА ОПЛАТУ'}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, letterSpacing: '-0.5px' }}>№{numericOrderId(invoice.order_id)}</div>
              <div style={{ fontSize: 12, color: SUB, marginTop: 4 }}>
                {new Date(invoice.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${statusColor}`, background: `${statusColor}18` }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: statusColor, fontWeight: 700 }}>{STATUS_LABELS[invoice.status] ?? invoice.status}</span>
            </div>
          </div>
        </div>

        {/* Line items */}
        {items.length > 0 && (
          <div style={{ background: CARD, margin: '12px 0 0', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
            <div style={{ padding: '14px 24px 10px', fontSize: 11, color: SUB, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.7px' }}>Состав работ и материалов</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 16px', padding: '0 24px 8px', fontSize: 11, color: SUB }}>
              <span>Наименование</span><span style={{ textAlign: 'right' }}>Кол-во</span><span style={{ textAlign: 'right', minWidth: 80 }}>Сумма</span>
            </div>
            <div style={{ borderTop: `1px solid ${BORDER}` }}>
              {items.map((item, i) => (
                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 16px', padding: '13px 24px', background: i % 2 === 0 ? CARD : '#F9FAFB', borderBottom: i < items.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                  <div>
                    <div style={{ fontSize: 14, color: TEXT, fontWeight: 500 }}>{item.title}</div>
                    <div style={{ fontSize: 11, color: SUB, marginTop: 2 }}>{fmt(item.unit_price)} ₽ / ед.</div>
                  </div>
                  <div style={{ fontSize: 14, color: SUB, textAlign: 'right', alignSelf: 'center' }}>{item.quantity}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: TEXT, textAlign: 'right', minWidth: 80, alignSelf: 'center' }}>{fmt(item.quantity * item.unit_price)} ₽</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total */}
        <div style={{ background: isPaid ? '#F0FDF4' : CARD, border: `1px solid ${isPaid ? '#86EFAC' : BORDER}`, margin: '12px 16px 0', borderRadius: 14, padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: SUB, fontWeight: 600 }}>ИТОГО К ОПЛАТЕ</span>
          <span style={{ fontSize: 28, fontWeight: 800, color: isPaid ? '#16A34A' : TEXT, letterSpacing: '-0.5px' }}>{fmt(total)} ₽</span>
        </div>

        {/* Paid badge */}
        {isPaid && invoice.payment_method && (
          <div style={{ margin: '10px 16px 0', padding: '14px 18px', borderRadius: 12, background: '#F0FDF4', border: '1px solid #86EFAC', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#16A34A', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontSize: 18 }}>✓</span>
            </div>
            <div>
              <div style={{ fontSize: 14, color: '#15803D', fontWeight: 700 }}>Счёт оплачен</div>
              <div style={{ fontSize: 12, color: '#4ADE80', marginTop: 2 }}>
                {PAYMENT_LABELS[invoice.payment_method]}{invoice.paid_at ? ` · ${new Date(invoice.paid_at).toLocaleDateString('ru-RU')}` : ''}
              </div>
            </div>
          </div>
        )}

        {/* Bank details */}
        {!isAkt && !isPaid && hasBankDetails && (
          <div style={{ margin: '16px 16px 0', padding: '16px 18px', borderRadius: 12, background: CARD, border: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 11, color: SUB, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.7px', marginBottom: 12 }}>Реквизиты для перевода</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pd?.bank_name && <ReqRow label="Банк" value={pd.bank_name} />}
              {pd?.account   && <ReqRow label="Р/счёт" value={pd.account} mono />}
              {pd?.bik       && <ReqRow label="БИК" value={pd.bik} mono />}
              {pd?.inn       && <ReqRow label="ИНН" value={pd.inn} mono />}
              {pd?.kpp       && <ReqRow label="КПП" value={pd.kpp} mono />}
            </div>
          </div>
        )}

        {/* SBP */}
        {!isAkt && !isPaid && hasSbp && (
          <div style={{ margin: '10px 16px 0', padding: '14px 18px', borderRadius: 12, background: CARD, border: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#F3F4F6', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 22 }}>📱</span>
            </div>
            <div>
              <div style={{ fontSize: 11, color: SUB, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 3 }}>Оплата по СБП</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: TEXT }}>{pd!.sbp_phone}</div>
              <div style={{ fontSize: 11, color: SUB, marginTop: 2 }}>Сумма: {fmt(total)} ₽</div>
            </div>
          </div>
        )}

        {/* Подписи — только для акта */}
        {isAkt && (
          <div style={{ margin: '28px 16px 0', display: 'flex', gap: 24, position: 'relative' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              {/* подпись над линией */}
              {branding.signature_url && (
                <img
                  src={branding.signature_url}
                  alt="Подпись"
                  style={{ position: 'absolute', bottom: 22, left: 0, maxHeight: 48, maxWidth: 150, objectFit: 'contain' }}
                />
              )}
              {/* печать — накрывает зону подписи */}
              {branding.stamp_url && (
                <img
                  src={branding.stamp_url}
                  alt="Печать"
                  style={{ position: 'absolute', bottom: -10, left: 55, maxHeight: 140, maxWidth: 140, objectFit: 'contain', opacity: 0.9, pointerEvents: 'none' }}
                />
              )}
              <div style={{ borderTop: `1px solid ${TEXT}`, paddingTop: 6, fontSize: 11, color: SUB }}>Исполнитель</div>
              <div style={{ fontSize: 12, color: TEXT, marginTop: 4 }}>{companyName}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ borderTop: `1px solid ${TEXT}`, paddingTop: 6, fontSize: 11, color: SUB }}>Заказчик</div>
              <div style={{ fontSize: 12, color: TEXT, marginTop: 4 }}>{clientName || '\u00A0'}</div>
            </div>
          </div>
        )}

        {/* Печать на оплаченном счёте (без режима акта) */}
        {!isAkt && isPaid && branding.stamp_url && (
          <div style={{ margin: '16px 16px 0', display: 'flex', justifyContent: 'flex-end', paddingRight: 8 }}>
            <img src={branding.stamp_url} alt="Печать" style={{ maxHeight: 140, maxWidth: 140, objectFit: 'contain', opacity: 0.9 }} />
          </div>
        )}

        {/* Footer */}
        <div style={{ margin: '24px 16px 0', paddingTop: 16, borderTop: `1px solid ${BORDER}`, fontSize: 11, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.7 }}>
          Документ сформирован автоматически · Кворум
        </div>
      </div>
    </div>
  );
}
